import * as amplitude from '@amplitude/analytics-browser';

import { SnowplowService } from 'wanderu-service-analytics';

function detailsButtonClicked({
    action,
    breakpoint,
    carrierCode,
    funnelStage,
    isRouted,
    seats,
    tripId,
    position,
    variant,
}: {
    action: string;
    breakpoint: string;
    carrierCode?: string;
    funnelStage: string;
    isRouted: boolean;
    seats: number;
    tripId: string;
    position?: number;
    variant: string;
}) {
    const property = { trip_id: tripId };

    SnowplowService.fireStructuredEventWithBatchId({
        action: 'click',
        category: variant === 'tripSummary'
            ? 'react_tripsummary'
            : 'react_search',
        label: `${action}_details`,
        property,
    });

    amplitude.track('trip_card_interacted', {
        breakpoint,
        carrier_code: carrierCode,
        funnel_stage: funnelStage,
        interaction_type: `${action}_itinerary`,
        is_routed: isRouted,
        pax_count: seats,
        results_position: typeof position === 'number'
            ? position + 1
            : undefined,
        trip_id: tripId,
    });
}

function seeMoreFeaturesHovered({
    breakpoint,
    carrierCode,
    funnelStage,
    isRouted,
    seats,
    tripId,
    position,
    variant,
}: {
    breakpoint: string;
    carrierCode?: string;
    funnelStage: string;
    isRouted: boolean;
    seats: number;
    tripId: string;
    position?: number;
    variant: string;
}) {
    const property = { trip_id: tripId };

    SnowplowService.fireStructuredEventWithBatchId({
        action: 'hover',
        category: variant === 'tripSummary'
            ? 'react_tripsummary'
            : 'react_search',
        label: 'see_more_amenities',
        property,
    });

    amplitude.track('trip_card_interacted', {
        breakpoint,
        carrier_code: carrierCode,
        funnel_stage: funnelStage,
        interaction_method: 'hover',
        interaction_type: 'view_amenities',
        is_routed: isRouted,
        pax_count: seats,
        results_position: typeof position === 'number'
            ? position + 1
            : undefined,
        trip_id: tripId,
    });
}

function seeMoreFeaturesClicked({
    breakpoint,
    carrierCode,
    funnelStage,
    isRouted,
    seats,
    tripId,
    position,
    variant,
}: {
    breakpoint: string;
    carrierCode?: string;
    funnelStage: string;
    isRouted: boolean;
    seats: number;
    tripId: string;
    position?: number;
    variant: string;
}) {
    const property = { trip_id: tripId };

    SnowplowService.fireStructuredEventWithBatchId({
        action: 'click',
        category: variant === 'tripSummary'
            ? 'react_tripsummary'
            : 'react_search',
        label: 'see_more_amenities',
        property,
    });

    amplitude.track('trip_card_interacted', {
        breakpoint,
        carrier_code: carrierCode,
        funnel_stage: funnelStage,
        interaction_method: 'click',
        interaction_type: 'view_amenities',
        is_routed: isRouted,
        pax_count: seats,
        results_position: typeof position === 'number'
            ? position + 1
            : undefined,
        trip_id: tripId,
    });
}

function carrierLinkSelected({
    breakpoint,
    carrierCode,
    carrierName,
    funnelStage,
    isRouted,
    rating,
    seats,
    selectsTrip,
    tripId,
    position,
    variant,
}: {
    breakpoint: string;
    carrierCode?: string;
    carrierName?: string;
    funnelStage: string;
    isRouted: boolean;
    rating: boolean;
    seats: number;
    selectsTrip: boolean;
    tripId: string;
    position?: number;
    variant: string;
}) {
    const label = rating
        ? 'carrier_rating_link'
        : 'carrier_logo_link';

    const property = { carrier_name: carrierName };

    SnowplowService.fireStructuredEventWithBatchId({
        action: 'click',
        category: variant === 'tripSummary'
            ? 'react_tripsummary'
            : 'react_search',
        label,
        property,
    });

    amplitude.track('trip_card_interacted', {
        breakpoint,
        carrier_code: carrierCode,
        funnel_stage: funnelStage,
        interaction_method: 'click',
        interaction_type: rating
            ? 'carrier_rating_clicked'
            : 'carrier_logo_clicked',
        is_routed: isRouted,
        pax_count: seats,
        results_position: typeof position === 'number'
            ? position + 1
            : undefined,
        selects_trip: selectsTrip,
        trip_id: tripId,
    });
}

function fareClassSelected({ fareClassCode, fareClassType, tripId }: {
    fareClassCode: string;
    fareClassType: string;
    tripId: string;
}) {
    const property = {
        fare_class_code: fareClassCode,
        fare_class_type: fareClassType,
        trip_id: tripId,
    };

    SnowplowService.fireStructuredEventWithBatchId({
        action: 'click',
        category: 'react_tripsummary',
        label: 'fare_class_selected',
        property,
    });

    amplitude.track('fare_class_selected clicked', property);
}

function nearbyLocationClicked({
    breakpoint,
    carrierCode,
    cityName,
    funnelStage,
    isRouted,
    position,
    region,
    seats,
    tripId,
    type,
}: {
    breakpoint: string;
    carrierCode?: string;
    cityName: string;
    funnelStage: string;
    isRouted: boolean;
    position?: number;
    region: string;
    seats: number;
    tripId: string;
    type: 'depart' | 'arrive';
    }) {
    amplitude.track('trip_card_interacted', {
        breakpoint,
        carrier_code: carrierCode,
        city_name: cityName,
        funnel_stage: funnelStage,
        interaction_type: 'nearby_city_clicked',
        is_routed: isRouted,
        pax_count: seats,
        region,
        results_position: typeof position === 'number'
            ? position + 1
            : undefined,
        station_type: type,
        trip_id: tripId,
    });
}

function stationNameClicked({
    breakpoint,
    carrierCode,
    funnelStage,
    isRouted,
    name,
    position,
    seats,
    tripId,
    type,
    variant,
}: {
    breakpoint: string;
    carrierCode?: string;
    funnelStage: string;
    isRouted: boolean;
    name: string;
    position?: number;
    seats: number;
    tripId: string;
    type: 'depart' | 'arrive';
    variant: string;
}) {
    amplitude.track('trip_card_interacted', {
        breakpoint,
        carrier_code: carrierCode,
        funnel_stage: funnelStage,
        interaction_type: 'station_name_clicked',
        is_routed: isRouted,
        pax_count: seats,
        results_position: typeof position === 'number'
            ? position + 1
            : undefined,
        station_name: name,
        station_type: type,
        trip_id: tripId,
        variant,
    });
}

export default {
    carrierLinkSelected,
    detailsButtonClicked,
    fareClassSelected,
    nearbyLocationClicked,
    seeMoreFeaturesClicked,
    seeMoreFeaturesHovered,
    stationNameClicked,
};
